import { TemplateFormatterType } from '../types';
import { formatDate } from './date/format-date';
import { formatCurrency } from './format-currency';
import { formatNumber } from './format-number';
import { formatPersonName } from './format-person-name';
/**
 * The regex to find template placeholders within a string of text.
 * Basically will match anything that starts with '${' and ends with '}', conservatively
 * (with a few extra checks for what is between the two curly braces).
 */
export const templatePlaceholderRegex = /\${([^{\]+[}]+)}/g;
/**
 * Extracts the parameter key from the template placeholder.
 * i.e. ${a} -> a
 *
 * @param placeholder The template placeholder (i.e. ${a})
 */
export const getParamKeyFromTemplatePlaceholder = (placeholder) => {
    return placeholder.substr(2, placeholder.length - 3).trim();
};
/**
 * Formats a given template string and parameters as a string.
 *
 * @hidden
 * @param template The template string to be formatted.
 * @param parameters The parameters to be used.
 * @param options The template format options to be used.
 * @returns The formatted template string.
 */
export const formatTemplate = (template, parameters, options) => {
    return template.replace(templatePlaceholderRegex, match => {
        const parameterString = getParamKeyFromTemplatePlaceholder(match);
        return getParameterValueFromString(parameterString, parameters, options);
    });
};
/**
 * Gets the value of a parameter from the provided string and parameters.
 * @param parameter The provided parameter
 * @param parameters The provided parameters
 * @param formatterConfig The formatter config (optional)
 */
const getParameterValueFromString = (parameter, parameters, options) => {
    const properties = parameter.split('|');
    const path = properties[0];
    const formatterTypeKey = properties[1];
    const formatString = properties[2];
    const value = getParameterValueFromPath(path, parameters);
    return formatterTypeKey ? formatValue(value, options, formatterTypeKey, formatString) : value;
};
/**
 * Gets the parameter value based on it's path string.
 * Return empty string as a fallback if the value does not exist.
 * @param path The path string of the parameter (e.g. 'date.shortDate')
 * @param parameters The parameters provided to formatTemplate
 */
const getParameterValueFromPath = (path, parameters) => {
    return path.split('.').reduce((res, key) => {
        const value = res[key];
        if (value === undefined || value === null) {
            return '';
        }
        return value;
    }, parameters);
};
/**
 * Formats a value using a specified formatter type and optional format string
 * @param value The value to be formatted
 * @param options The options to be used
 * @param formatterTypeKey The formatter type key
 * @param formatString The format string (optional)
 */
const formatValue = (value, options, formatterTypeKey, formatString) => {
    const formatterType = getTemplateFormatterTypeFromKey(formatterTypeKey);
    switch (formatterType) {
        case TemplateFormatterType.Date:
            return formatDate(value, getMergedDateFormatOptions(options.date, formatString));
        case TemplateFormatterType.Number:
            return formatNumber(value, options.number);
        case TemplateFormatterType.Currency:
            return formatCurrency(value, options.currency);
        case TemplateFormatterType.PersonName:
            return formatPersonName(value, options) || '';
        default:
            throw new Error(`"${formatterTypeKey}" is not a valid TemplateFormatterType.`);
    }
};
/**
 * Gets the formatter type from a given key. Throws an error if the key is invalid
 */
const getTemplateFormatterTypeFromKey = (formatterTypeKey) => {
    const formatterTypeKeyInLowerCase = formatterTypeKey.toLowerCase();
    if (Object.values(TemplateFormatterType).includes(formatterTypeKeyInLowerCase)) {
        return formatterTypeKeyInLowerCase;
    }
    return undefined;
};
const getMergedDateFormatOptions = (options, formatString) => {
    if (!formatString || formatString.length === 0) {
        return options;
    }
    return Object.assign(Object.assign({}, options), { format: formatString });
};
//# sourceMappingURL=format-template.js.map